import { useState } from "react";
import {
  CheckCircle2,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Award,
  Target,
  FileText,
  ChevronDown,
  ChevronRight,
  BarChart3,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

interface AnswerDetail {
  questionId: string;
  name: string;
  practice: string;
  score: number;
  details: string;
}

interface SDLAssessmentFeedbackProps {
  productId: string;
  productName: string;
  period: string;
  overallScore: number;
  answers: AnswerDetail[];
  previousAnswers?: Record<string, { score: number | null; details: string }>;
  previousPeriod?: string | null;
  onViewAnswers?: () => void;
}

type MaturityLevel = {
  min: number;
  max: number;
  label: string;
  color: string;
  bg: string;
  ring: string;
  bar: string;
};

const maturityLevels: MaturityLevel[] = [
  {
    min: 0,
    max: 1.0,
    label: "Initial",
    color: "text-red-600 dark:text-red-400",
    bg: "bg-red-50 dark:bg-red-900/20",
    ring: "ring-red-200 dark:ring-red-800",
    bar: "from-red-500 to-red-400",
  },
  {
    min: 1.0,
    max: 2.0,
    label: "Developing",
    color: "text-orange-600 dark:text-orange-400",
    bg: "bg-orange-50 dark:bg-orange-900/20",
    ring: "ring-orange-200 dark:ring-orange-800",
    bar: "from-orange-500 to-orange-400",
  },
  {
    min: 2.0,
    max: 3.0,
    label: "Managed",
    color: "text-yellow-600 dark:text-yellow-400",
    bg: "bg-yellow-50 dark:bg-yellow-900/20",
    ring: "ring-yellow-200 dark:ring-yellow-800",
    bar: "from-yellow-500 to-yellow-400",
  },
  {
    min: 3.0,
    max: 4.01,
    label: "Optimised",
    color: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-50 dark:bg-emerald-900/20",
    ring: "ring-emerald-200 dark:ring-emerald-800",
    bar: "from-emerald-500 to-emerald-400",
  },
];

function getMaturityLevel(score: number): MaturityLevel {
  const found = maturityLevels.find((l) => score >= l.min && score < l.max);
  if (found) return found;
  return maturityLevels[0] as MaturityLevel;
}

function getScoreBadgeStyle(score: number): string {
  if (score >= 3.0)
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400";
  if (score >= 2.0)
    return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400";
  if (score >= 1.0)
    return "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400";
  return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
}

function getScoreBarColor(score: number): string {
  if (score >= 3.0) return "bg-emerald-500";
  if (score >= 2.0) return "bg-yellow-500";
  if (score >= 1.0) return "bg-orange-500";
  return "bg-red-500";
}

function getPracticeScores(answers: AnswerDetail[]) {
  const grouped: Record<
    string,
    { total: number; count: number; questions: AnswerDetail[] }
  > = {};
  answers.forEach((a) => {
    if (!grouped[a.practice])
      grouped[a.practice] = { total: 0, count: 0, questions: [] };
    const group = grouped[a.practice]!;
    group.total += a.score;
    group.count += 1;
    group.questions.push(a);
  });
  return Object.entries(grouped)
    .map(([practice, data]) => ({
      practice,
      avgScore: data.total / data.count,
      count: data.count,
      questions: data.questions.sort((a, b) => a.score - b.score),
    }))
    .sort((a, b) => a.avgScore - b.avgScore);
}

export default function SDLAssessmentFeedback({
  productId,
  productName,
  period,
  overallScore,
  answers,
  previousAnswers,
  previousPeriod,
  onViewAnswers,
}: SDLAssessmentFeedbackProps) {
  const navigate = useNavigate();
  const level = getMaturityLevel(overallScore);
  const percentage = (overallScore / 4) * 100;
  const practiceScores = getPracticeScores(answers);
  const weakAreas = practiceScores.filter((p) => p.avgScore < 2);
  const strongAreas = practiceScores.filter((p) => p.avgScore >= 3);

  const [expandedPractice, setExpandedPractice] = useState<string | null>(null);
  const [showQuestionTable, setShowQuestionTable] = useState(false);

  // Calculate improvement from previous period
  const hasPrevious = previousAnswers && previousPeriod;
  let previousOverall: number | null = null;
  let scoreDelta: number | null = null;
  if (hasPrevious) {
    const prevScored = Object.values(previousAnswers).filter(
      (a) => a.score !== null && a.score !== -1,
    );
    if (prevScored.length > 0) {
      previousOverall =
        prevScored.reduce((acc, a) => acc + (a.score ?? 0), 0) /
        prevScored.length;
      scoreDelta = overallScore - previousOverall;
    }
  }

  return (
    <div className="flex-1 py-6 overflow-y-auto">
      <div className="w-full max-w-3xl mx-auto px-4">
        {/* Success header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
            <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {onViewAnswers ? "Assessment Results" : "Assessment Submitted!"}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {productName} &middot; {period}
          </p>
        </div>

        {/* Score card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden mb-6">
          {/* Gradient top bar */}
          <div className={`h-1.5 w-full bg-gradient-to-r ${level.bar}`} />

          <div className="p-6">
            <div className="flex items-center gap-6">
              {/* Score circle */}
              <div className="flex-shrink-0 relative">
                <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="8"
                    className="text-gray-100 dark:text-gray-800"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    fill="none"
                    strokeWidth="8"
                    strokeDasharray={`${percentage * 2.64} ${264 - percentage * 2.64}`}
                    strokeLinecap="round"
                    className={level.color}
                    stroke="currentColor"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span
                    className="text-2xl font-extrabold text-gray-900 dark:text-gray-100"
                    style={{ fontFeatureSettings: '"tnum"' }}
                  >
                    {overallScore.toFixed(1)}
                  </span>
                  <span className="text-[10px] text-gray-400 font-medium">
                    / 4.0
                  </span>
                </div>
              </div>

              {/* Level info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Award className={`h-5 w-5 ${level.color}`} />
                  <span className={`text-lg font-bold ${level.color}`}>
                    {level.label}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                  Your overall maturity level based on {answers.length} assessed
                  questions across {practiceScores.length} practices.
                </p>

                {/* Delta from previous */}
                {scoreDelta !== null && previousOverall !== null && (
                  <div
                    className={`inline-flex items-center gap-1.5 mt-2 text-xs font-semibold px-2.5 py-1 rounded-full ${
                      scoreDelta > 0
                        ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300"
                        : scoreDelta < 0
                          ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    {scoreDelta > 0 ? (
                      <TrendingUp className="h-3.5 w-3.5" />
                    ) : scoreDelta < 0 ? (
                      <TrendingDown className="h-3.5 w-3.5" />
                    ) : null}
                    {scoreDelta > 0 ? "+" : ""}
                    {scoreDelta.toFixed(2)} from {previousPeriod}
                    <span className="text-gray-400 dark:text-gray-500 font-normal ml-0.5">
                      ({previousOverall.toFixed(1)})
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Practice Area Scores — Interactive Breakdown */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden mb-6">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
            <BarChart3 className="h-4.5 w-4.5 text-blue-500" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Practice Area Scores
            </h3>
            <span className="ml-auto text-[11px] text-gray-400">
              {practiceScores.length} areas · Click to expand
            </span>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {practiceScores.map((p) => {
              const pLevel = getMaturityLevel(p.avgScore);
              const pPct = (p.avgScore / 4) * 100;
              const isExpanded = expandedPractice === p.practice;
              return (
                <div key={p.practice}>
                  <button
                    onClick={() =>
                      setExpandedPractice(isExpanded ? null : p.practice)
                    }
                    className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {p.practice}
                        </span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span
                            className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${getScoreBadgeStyle(p.avgScore)}`}
                          >
                            {pLevel.label}
                          </span>
                          <span className="text-sm font-bold text-gray-700 dark:text-gray-300 tabular-nums w-12 text-right">
                            {p.avgScore.toFixed(1)}/4
                          </span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-500 ${getScoreBarColor(p.avgScore)}`}
                          style={{ width: `${pPct}%` }}
                        />
                      </div>
                    </div>
                  </button>

                  {/* Expanded question list */}
                  {isExpanded && (
                    <div className="bg-gray-50 dark:bg-gray-800/30 px-5 py-2 border-t border-gray-100 dark:border-gray-800">
                      <table className="w-full">
                        <thead>
                          <tr className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            <th className="text-left py-1.5 font-medium">
                              Question
                            </th>
                            <th className="text-right py-1.5 font-medium w-16">
                              Score
                            </th>
                            <th className="text-right py-1.5 font-medium w-20">
                              vs Avg
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                          {p.questions.map((q) => {
                            const diff = q.score - p.avgScore;
                            return (
                              <tr key={q.questionId}>
                                <td className="py-2 pr-3">
                                  <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {q.name}
                                  </span>
                                </td>
                                <td className="py-2 text-right">
                                  <span
                                    className={`inline-flex items-center justify-center w-8 h-6 rounded text-xs font-bold ${getScoreBadgeStyle(q.score)}`}
                                  >
                                    {q.score}
                                  </span>
                                </td>
                                <td className="py-2 text-right">
                                  <span
                                    className={`text-[11px] font-medium ${
                                      diff > 0
                                        ? "text-green-600 dark:text-green-400"
                                        : diff < 0
                                          ? "text-red-500 dark:text-red-400"
                                          : "text-gray-400"
                                    }`}
                                  >
                                    {diff > 0 ? "+" : ""}
                                    {diff.toFixed(1)}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Question-Level Scoring Table */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden mb-6">
          <button
            onClick={() => setShowQuestionTable(!showQuestionTable)}
            className="w-full px-5 py-4 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
          >
            <Target className="h-4.5 w-4.5 text-purple-500" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Question-Level Details
            </h3>
            <span className="ml-auto text-[11px] text-gray-400 mr-1">
              {answers.length} questions
            </span>
            {showQuestionTable ? (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-400" />
            )}
          </button>

          {showQuestionTable && (
            <div className="border-t border-gray-200 dark:border-gray-700 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <th className="text-left pl-5 pr-2 py-2.5 w-8">#</th>
                    <th className="text-left px-2 py-2.5">Question</th>
                    <th className="text-left px-2 py-2.5">Practice Area</th>
                    <th className="text-center px-2 py-2.5 w-16">Score</th>
                    <th className="text-center pr-5 pl-2 py-2.5 w-20">
                      Area Avg
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {answers.map((a, idx) => {
                    const pa = practiceScores.find(
                      (p) => p.practice === a.practice,
                    );
                    const areaAvg = pa ? pa.avgScore : 0;
                    const isBelow = a.score < areaAvg;
                    return (
                      <tr
                        key={a.questionId}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors ${isBelow ? "bg-red-50/30 dark:bg-red-900/5" : ""}`}
                      >
                        <td className="pl-5 pr-2 py-2.5 text-[11px] text-gray-400 font-mono">
                          {idx + 1}
                        </td>
                        <td className="px-2 py-2.5">
                          <span className="text-sm text-gray-800 dark:text-gray-200">
                            {a.name}
                          </span>
                        </td>
                        <td className="px-2 py-2.5">
                          <span className="text-[11px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full whitespace-nowrap">
                            {a.practice}
                          </span>
                        </td>
                        <td className="px-2 py-2.5 text-center">
                          <span
                            className={`inline-flex items-center justify-center w-8 h-6 rounded-md text-xs font-bold ${getScoreBadgeStyle(a.score)}`}
                          >
                            {a.score}
                          </span>
                        </td>
                        <td className="pr-5 pl-2 py-2.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400 tabular-nums">
                              {areaAvg.toFixed(1)}
                            </span>
                            {isBelow && (
                              <span
                                className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0"
                                title="Below area average"
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Areas for improvement */}
        {weakAreas.length > 0 && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Areas for Improvement
              </h3>
            </div>
            <div className="p-4">
              <div className="space-y-2">
                {weakAreas.map((area) => {
                  const weakest = area.questions[0];
                  return (
                    <div
                      key={area.practice}
                      className="flex items-start gap-3 p-3 rounded-xl bg-amber-50/50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20"
                    >
                      <div
                        className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${getScoreBadgeStyle(area.avgScore)}`}
                      >
                        {area.avgScore.toFixed(1)}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {area.practice}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {area.count} question{area.count > 1 ? "s" : ""}
                          {weakest && (
                            <>
                              {" "}
                              · Lowest:{" "}
                              <span className="font-medium text-amber-600 dark:text-amber-400">
                                "{weakest.name}" ({weakest.score}/4)
                              </span>
                            </>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Focus on:{" "}
                          {area.questions
                            .filter((q) => q.score < 2)
                            .map((q) => q.name)
                            .join(", ")}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Strong areas */}
        {strongAreas.length > 0 && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-green-500" />
                Strong Areas
              </h3>
            </div>
            <div className="p-4">
              <div className="flex flex-wrap gap-2">
                {strongAreas.map((area) => (
                  <span
                    key={area.practice}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 dark:bg-green-900/20 text-xs font-medium text-green-700 dark:text-green-300 ring-1 ring-green-200/60 dark:ring-green-800/40"
                  >
                    <CheckCircle2 className="h-3 w-3" />
                    {area.practice} ({area.avgScore.toFixed(1)})
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2 pb-8">
          <button
            onClick={() => navigate(`/products/${productId}`)}
            className="btn btn-primary text-sm w-full sm:w-auto"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back to Product
          </button>
          {onViewAnswers && (
            <button
              onClick={onViewAnswers}
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors w-full sm:w-auto"
            >
              <FileText className="h-4 w-4 mr-1.5" />
              View Answers
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
